import React from 'react'

import { Box, Flex, InputGroup } from '@clear/design-system'
import {
  DeepMap,
  FieldError,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
} from 'react-hook-form'
import { Form, StepSubTitle } from '@components/layout/Form'
import { yupResolver } from '@hookform/resolvers/yup'
import PageContent from './layout/PageContent'
import { ContinueButton } from './FormButtons'
import { authSchemaWithoutTerms } from '@validators/schema/enroll/authSchema'
import { PageHeading } from './layout/Heading'

export type ContactInfoFields = Partial<{
  emailAddress: string
  phoneNumber: string
  countryCode: string
}>

/** contact info submitted that passes schema + defaults */
export type ValidatedContactInfo = Required<ContactInfoFields>

export type ContactInfoProps = {
  onSubmit: (formData: ValidatedContactInfo) => void
  pageTitle: string
  variant?: 'default' | 'lookup'
}

/** page component to collect phone and email from users */
const CollectContactInfo: React.FC<ContactInfoProps> = ({
  onSubmit,
  variant = 'default',
  pageTitle,
}) => {
  const { register, handleSubmit, formState } = useForm<ContactInfoFields>({
    resolver: yupResolver(authSchemaWithoutTerms),
    mode: 'onChange',
    defaultValues: {
      countryCode: '+1',
    },
  })
  const { errors } = formState

  return (
    <PageContent>
      {variant === 'default' ? (
        <DefaultForm
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          register={register}
          errors={errors}
        />
      ) : (
        <LookupForm
          pageTitle={pageTitle}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          register={register}
          errors={errors}
        />
      )}
    </PageContent>
  )
}

const DefaultForm: React.FC<{
  handleSubmit: UseFormHandleSubmit<ContactInfoFields>
  onSubmit: ContactInfoProps['onSubmit']
  register: UseFormRegister<ContactInfoFields>
  errors: DeepMap<ContactInfoFields, FieldError>
}> = ({ handleSubmit, onSubmit, register, errors }) => (
  <>
    <Box
      width={['95%', '572px']}
      marginLeft="auto"
      marginRight="auto"
      border="1px solid #CCCED1"
      borderRadius="8px"
      paddingX={['20px', '50px']}
      paddingY={['20px', '40px']}
      backgroundColor="neutralsWhite"
    >
      <Box
        className="pb-4 px-4"
        as="img"
        minWidth={0}
        mx="auto"
        src="/tsa-pre-logo-auth.svg"
        alt="TSA pre-check with CLEAR logo"
      />
      <StepSubTitle textAlign="center" fontSize={3}>
        Enter your email and mobile number. You'll be sent a one-time code to
        sign in / create an account
      </StepSubTitle>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <InputGroup
          data-cy="email"
          placeholder={'Email'}
          mb={2}
          {...register('emailAddress')}
          error={errors.emailAddress?.message}
          containerProps={{ padding: 0 }}
        />
        <Flex flexDirection="row">
          <InputGroup
            data-cy={'countryCode'}
            name="countryCode"
            disabled
            value="+1"
            containerProps={{
              padding: 0,
              width: '20%',
            }}
          />
          <InputGroup
            data-cy="phoneNumber"
            placeholder={'Phone number'}
            {...register('phoneNumber')}
            error={errors.phoneNumber?.message}
            containerProps={{
              padding: 0,
              marginLeft: '10px',
              width: '90%',
            }}
          />
        </Flex>
        <ContinueButton marginTop="15px" type="submit" />
      </Form>
    </Box>
  </>
)

const LookupForm: React.FC<{
  pageTitle: string
  handleSubmit: UseFormHandleSubmit<ContactInfoFields>
  onSubmit: ContactInfoProps['onSubmit']
  register: UseFormRegister<ContactInfoFields>
  errors: DeepMap<ContactInfoFields, FieldError>
}> = ({ pageTitle, handleSubmit, onSubmit, register, errors }) => (
  <>
    <PageHeading>{pageTitle}</PageHeading>
    <Box
      width={['95%', '572px']}
      marginLeft="auto"
      marginRight="auto"
      border="1px solid #CCCED1"
      borderRadius="8px"
      px="2rem"
      py="1.5rem"
      backgroundColor="neutralsWhite"
    >
      <Form onSubmit={handleSubmit(onSubmit)} sx={{ gap: '1.5rem' }}>
        <InputGroup
          data-cy="email"
          type="email"
          autoComplete="email"
          label="Email Address"
          {...register('emailAddress')}
          error={errors.emailAddress?.message}
          containerProps={{
            p: 0,
          }}
        />
        <InputGroup
          data-cy="phoneNumber"
          type="tel"
          autoComplete="tel"
          label="Phone Number"
          {...register('phoneNumber')}
          error={errors.phoneNumber?.message}
          containerProps={{
            p: 0,
          }}
        />
        <Flex justifyContent="flex-end">
          <ContinueButton
            maxWidth="230px"
            minWidth="200px"
            size="small"
            icon="chevron-right"
            iconPosition="right"
            type="submit"
          />
        </Flex>
      </Form>
    </Box>
  </>
)

export default CollectContactInfo
